import { apiClient } from './client';

export interface ReportFilters {
  contractId?: string;
  companyId?: string;
  startDate?: string;
  endDate?: string;
  year?: string;
  month?: string;
}

export interface TicketsReportFilters extends ReportFilters {
  contractId?: string;
  companyId?: string;
  startDate?: string;
  endDate?: string;
}

export interface ContractsReportFilters extends ReportFilters {
  companyId?: string;
}

export interface MonthlyConsumptionReportFilters extends ReportFilters {
  contractId?: string;
  companyId?: string;
  year?: string;
  month?: string;
}

export const reportsApi = {
  generateTicketsReport: async (filters: TicketsReportFilters): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (filters.contractId) searchParams.append('contractId', filters.contractId);
    if (filters.companyId) searchParams.append('companyId', filters.companyId);
    if (filters.startDate) searchParams.append('startDate', filters.startDate);
    if (filters.endDate) searchParams.append('endDate', filters.endDate);

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/reports/tickets${queryString ? `?${queryString}` : ''}`,
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds for PDF generation
      }
    );
    return response.data;
  },

  generateContractsReport: async (filters: ContractsReportFilters): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (filters.companyId) searchParams.append('companyId', filters.companyId);

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/reports/contracts${queryString ? `?${queryString}` : ''}`,
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds for PDF generation
      }
    );
    return response.data;
  },

  generateMonthlyConsumptionReport: async (
    filters: MonthlyConsumptionReportFilters
  ): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (filters.contractId) searchParams.append('contractId', filters.contractId);
    if (filters.companyId) searchParams.append('companyId', filters.companyId);
    if (filters.year) searchParams.append('year', filters.year);
    if (filters.month) searchParams.append('month', filters.month);

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/reports/monthly-consumption${queryString ? `?${queryString}` : ''}`,
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds for PDF generation
      }
    );
    return response.data;
  },
};

export interface ContractConsumptionFilters {
  contractId: string;
  /** YYYY-MM */
  startMonth?: string;
  /** YYYY-MM */
  endMonth?: string;
}

export type ProjectionStatus =
  | 'NO_CONTRACTED_HOURS'
  | 'EXCEEDED'
  | 'NO_RECENT_CONSUMPTION'
  | 'NO_END_DATE'
  | 'DEPLETES_BEFORE_END'
  | 'LASTS_UNTIL_END';

export interface ContractConsumptionDashboard {
  contract: {
    id: string;
    number: string;
    companyId: string;
    companyName: string;
    startDate: string | null;
    endDate: string | null;
    contractedHours: number;
    monthlyHoursLimit: number;
    accumulateHours: boolean;
    excludeProactiveTickets: boolean;
    isActive: boolean;
    daysToEnd: number | null;
  };
  period: { start: { year: number; month: number }; end: { year: number; month: number } };
  summary: {
    consumedHours: number;
    remainingHours: number | null;
    percentUsed: number | null;
    periodConsumedHours: number;
    periodWorkedHours: number;
    periodTicketCount: number;
    averageMonthlyHours: number;
    monthsOverLimit: number;
  };
  proactive: { totalWorkedHours: number; periodWorkedHours: number; periodTicketCount: number };
  projection: {
    status: ProjectionStatus;
    burnRateHours: number;
    depletionDate: string | null;
  };
  monthly: Array<{
    year: number;
    month: number;
    consumedHours: number;
    hoursLimit: number;
    ticketCount: number;
    cumulativeConsumedHours: number;
    cumulativeLimitHours: number;
    overLimit: boolean;
  }>;
  topTickets: Array<{
    number: string;
    title: string;
    date: string;
    hours: number;
    userName: string | null;
  }>;
  byUser: Array<{ name: string; hours: number; ticketCount: number }>;
  alerts: Array<{ severity: 'critical' | 'warning' | 'info'; message: string }>;
  generatedAt: string;
}

function contractConsumptionQuery(filters: ContractConsumptionFilters) {
  const params = new URLSearchParams({ contractId: filters.contractId });
  if (filters.startMonth) params.append('startMonth', filters.startMonth);
  if (filters.endMonth) params.append('endMonth', filters.endMonth);
  return params.toString();
}

export const contractConsumptionApi = {
  get: async (filters: ContractConsumptionFilters): Promise<ContractConsumptionDashboard> => {
    const response = await apiClient.get(
      `/reports/contract-consumption?${contractConsumptionQuery(filters)}`
    );
    return response.data;
  },

  downloadPdf: async (filters: ContractConsumptionFilters): Promise<Blob> => {
    const response = await apiClient.get(
      `/reports/contract-consumption/pdf?${contractConsumptionQuery(filters)}`,
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
};

export type UsageStatus = 'NO_HOURS_BANK' | 'OVERUSED' | 'UNDERUSED' | 'ON_TRACK';
export type HealthStatus = 'critical' | 'warning' | 'healthy';

export interface ContractPortfolioRow {
  id: string;
  number: string;
  companyId: string;
  companyName: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  daysToEnd: number | null;
  contractedHours: number;
  monthlyHoursLimit: number;
  consumedHours: number;
  remainingHours: number | null;
  percentUsed: number | null;
  averageMonthlyHours: number;
  monthsOverLimit: number;
  projection: ContractConsumptionDashboard['projection'];
  alerts: ContractConsumptionDashboard['alerts'];
  termElapsedPercent: number | null;
  pace: number | null;
  usage: UsageStatus;
  health: HealthStatus;
}

export interface ContractPortfolio {
  generatedAt: string;
  kpis: {
    totalContracts: number;
    activeContracts: number;
    contractedHours: number;
    consumedHours: number;
    health: { critical: number; warning: number; healthy: number };
    usage: { overused: number; underused: number; onTrack: number; noHoursBank: number };
    expiring: { d30: number; d60: number; d90: number };
    expiredStillActive: number;
  };
  contracts: ContractPortfolioRow[];
}

export const contractPortfolioApi = {
  get: async (): Promise<ContractPortfolio> => {
    const response = await apiClient.get('/reports/contract-portfolio', { timeout: 60000 });
    return response.data;
  },
};

export interface ResourceAllocation {
  period: { start: string; end: string; capacityEnd: string | null; workdays: number };
  weeklyHours: number;
  hoursPerWorkday: number;
  summary: {
    analystCount: number;
    workedHours: number;
    capacityHours: number;
    occupancyPercent: number | null;
    clientCount: number;
    ticketCount: number;
  };
  analysts: Array<{
    id: string;
    name: string;
    groupName: string | null;
    workedHours: number;
    capacityHours: number;
    occupancyPercent: number | null;
    ticketCount: number;
    clients: Array<{ companyId: string; name: string; hours: number }>;
  }>;
  clients: Array<{
    companyId: string;
    name: string;
    hours: number;
    sharePercent: number;
    fte: number | null;
    ticketCount: number;
    analysts: Array<{ name: string; hours: number }>;
  }>;
  generatedAt: string;
}

export interface ResourceAllocationFilters {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  groupId?: string; // team id, or "none" for analysts without a team
}

export const resourceAllocationApi = {
  get: async (filters: ResourceAllocationFilters): Promise<ResourceAllocation> => {
    const response = await apiClient.get('/reports/resource-allocation', {
      params: filters,
      timeout: 60000,
    });
    return response.data;
  },
};
